import React, { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatBytes } from '../../../../../utils/mimetype';

const baseStyle = {
  borderWidth: 2,
  borderRadius: 2,
  borderColor: 'var(--muted)',
  borderStyle: 'dashed',
  backgroundColor: 'var(--background)',
  color: 'var(--muted-foreground)',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  opacity: 1
};

const focusedStyle = {
  borderColor: 'var(--muted-foreground)',
  opacity: 1
};

const acceptStyle = {
  borderColor: 'green',
  opacity: 1
};

const rejectStyle = {
  borderColor: '#ff1744',
  opacity: 1
};

const FormUpload = ({ isLoading, setFiles, maxFileSize }) => {
  const formatAcceptedObj = {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'image/gif': ['.gif'],
    'image/svg+xml': ['.svg']
  };
  const formatAccepted = Object.keys(formatAcceptedObj).reduce((acc, key) => {
    return [...acc, ...formatAcceptedObj[key]]
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    accept: formatAcceptedObj,
    maxSize: maxFileSize,
    onDrop: (acceptedFiles) => {
      setFiles((prev) =>
        prev
          .filter((item) =>
            acceptedFiles.length > 0 ? acceptedFiles?.some((file) => file.name !== item.name) : true
          )
          .concat(
            acceptedFiles.map((file) =>
              Object.assign(
                {
                  name: file.name,
                  path: file.path,
                  size: file.size,
                  type: file.type,
                  lastModified: file.lastModified
                },
                {
                  preview: URL.createObjectURL(file),
                  file
                }
              )
            )
          )
      );
      
    }
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {})
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  return (
    <>
      {isLoading ? (
          <></>
        ) : (
          <div
            {...getRootProps({
              style,
              disabled: isLoading,
              className: 'dropzone h-44 flex flex-col gap-0.5  border border-dashed relative cursor-pointer'
            })}
          >
            <input {...getInputProps()} />
            <FontAwesomeIcon icon={['fas', 'cloud-upload-alt']} size='3x' className='text-primary' />
            <p className='text-primary text-3xl text-center'>Upload photo</p>
            <div className='flex gap-1 items-center justify-center text-muted-foreground text-center text-sm'>
              <p className='text-muted-foreground text-sm text-center'>Supported format: </p>
              <div className='flex flex-wrap justify-center gap-1'>
                {formatAccepted.map((ext, index) => (
                  <b key={`format-${ext}-${index}`} className='text-primary border text-sm  px-1 py-0.5 rounded uppercase'>{ext}</b>
                ))}
              </div> 
            </div>
            <p className='text-muted-foreground text-center text-sm '>
              Size limit: <b className='text-secondary'>{formatBytes(maxFileSize, 0)}</b>
            </p>
            <div className='absolute bottom-0 left-0 right-0'>
              <p className='text-muted-foreground opacity-75 py-2 px-2 bg-muted  text-center text-sm '>
                Drag 'n' drop some file here, or click to select file
              </p>
            </div>
          </div>
        )}
    </>
  );
};

export default FormUpload;
